import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";

import {
  UpsertCreditAccountRecoveryRequest,
} from "../../../api/credit_account_recovery";

import {
  upsertCreditAccountRecovery,
} from "../logic/accountDetailsThunk";

import {
  getAccountDetailByAccountNumber,
} from "../../creditAccounts/logic/slice";

import { formatDateStringForDisplay } from "../../../lib/utils";
import { showErrorToast } from "../../../lib/toast";

interface AccountRecoveryProps {
  accountNumber: string;
}

const AccountRecovery: React.FC<AccountRecoveryProps> = ({
  accountNumber,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const accountDetails = useAppSelector((state) =>
    getAccountDetailByAccountNumber(state, accountNumber)
  );

  if (!accountDetails) {
    return null;
  }

  const recoveryDetails = accountDetails.accountOverview?.recovery_details;

  const as_of_date = recoveryDetails?.as_of_date;
  const total_recovery_amount = recoveryDetails?.total_recovery_amount;
  const post_npa_recovery_amount = recoveryDetails?.post_npa_recovery_amount;
  const extent_of_loss_to_bank = recoveryDetails?.extent_of_loss_to_bank;

  const handleSave = async (
    fieldKey: keyof UpsertCreditAccountRecoveryRequest,
    value: string | number | null
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      const defaultAsOfDate = as_of_date || todayStr;

      const request: UpsertCreditAccountRecoveryRequest = {
        as_of_date: defaultAsOfDate,
        total_recovery_amount: total_recovery_amount ?? null,
        post_npa_recovery_amount: post_npa_recovery_amount ?? null,
        extent_of_loss_to_bank: extent_of_loss_to_bank ?? null,
      };

      if (fieldKey === "as_of_date") {
        request.as_of_date = String(value || todayStr);
      } else {
        (request[fieldKey] as number | null) =
          value === null ? null : Number(value);
      }

      await dispatch(
        upsertCreditAccountRecovery({
          caseId: caseId!,
          accountNumber,
          request,
        })
      ).unwrap();

      setEditingField(null);
      return true;
    } catch (error) {
      showErrorToast("Failed to update recovery field. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Recovery">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="As Of Date"
          value={as_of_date ?? null}
          type="date"
          isEditable={true}
          isEditing={editingField === "as_of_date"}
          onEditStart={() => setEditingField("as_of_date")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("as_of_date", value)}
          displayValue={
            as_of_date ? formatDateStringForDisplay(as_of_date) : "--"
          }
          disabled={isSaving}
        />

        <EditableField
          label="Total Recovery Amount (in ₹)"
          value={total_recovery_amount ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "total_recovery_amount"}
          onEditStart={() => setEditingField("total_recovery_amount")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("total_recovery_amount", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Post-NPA Recovery (in ₹)"
          value={post_npa_recovery_amount ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "post_npa_recovery_amount"}
          onEditStart={() => setEditingField("post_npa_recovery_amount")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("post_npa_recovery_amount", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Extent of Loss to Bank (in ₹)"
          value={extent_of_loss_to_bank ?? null}
          type="currency"
          isEditable={true}
          isEditing={editingField === "extent_of_loss_to_bank"}
          onEditStart={() => setEditingField("extent_of_loss_to_bank")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("extent_of_loss_to_bank", value)}
          disabled={isSaving}
        />
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default AccountRecovery;